<template>
  <div class="max-w-7xl mx-auto pb-10 relative">

    <div
      class="space-y-6 transition-all duration-300 animate-fade-in"
      :class="{ 'blur-sm pointer-events-none opacity-50': showModal }"
    >

      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Fuentes de Financiamiento</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm">Parametrización del origen de los fondos para el otorgamiento de créditos.</p>
        </div>
        <button
          @click="openCreate"
          class="flex items-center gap-2 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl shadow-lg shadow-brand-500/30 transition-all active:scale-95"
        >
          <Plus class="w-5 h-5" /> Nueva Fuente
        </button>
      </div>

      <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row gap-4">
        <div class="relative flex-1">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar por nombre de la fuente..."
            class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
          >
        </div>
        <select v-model="statusFilter" class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none">
          <option value="">Todos los Estados</option>
          <option value="ACTIVE">Activos</option>
          <option value="INACTIVE">Inactivos</option>
        </select>
      </div>

      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th class="px-6 py-4 font-semibold">Fuente de Fondos</th>
                <th class="px-6 py-4 font-semibold">Descripción</th>
                <th class="px-6 py-4 font-semibold">Estado</th>
                <th class="px-6 py-4 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">

              <tr v-if="isLoading">
                <td colspan="4" class="px-6 py-8 text-center text-slate-500">Cargando información...</td>
              </tr>

              <tr v-else-if="filteredList.length === 0">
                <td colspan="4" class="px-6 py-8 text-center text-slate-500">No hay fuentes de financiamiento que coincidan.</td>
              </tr>

              <tr v-else v-for="item in filteredList" :key="item.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">

                <td class="px-6 py-4">
                  <div class="flex items-center gap-4">
                    <div class="w-10 h-10 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 flex items-center justify-center font-bold text-xs uppercase">
                      <Landmark class="w-4 h-4" />
                    </div>
                    <div>
                      <div class="font-bold text-slate-900 dark:text-slate-100 line-clamp-1">{{ item.name }}</div>
                      <div class="text-[10px] font-mono text-slate-400 mt-0.5">ID: {{ item.id.substring(0,8) }}...</div>
                    </div>
                  </div>
                </td>

                <td class="px-6 py-4 text-slate-600 dark:text-slate-400">
                  <div class="text-xs truncate max-w-xs" :title="item.description">{{ item.description || 'Sin descripción' }}</div>
                </td>

                <td class="px-6 py-4">
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                    :class="getStatusClasses(item.status)">
                    <span class="w-1.5 h-1.5 rounded-full" :class="getStatusDot(item.status)"></span>
                    {{ getStatusLabel(item.status) }}
                  </span>
                </td>

                <td class="px-6 py-4 text-right">
                  <div class="flex justify-end gap-2">
                    <button @click="openEdit(item)" class="p-2 text-slate-400 hover:text-white hover:bg-brand-50 rounded-lg transition-colors" title="Editar Fuente">
                      <FileText class="w-4 h-4" />
                    </button>
                    <button @click="deleteItem(item)" class="p-2 text-slate-400 hover:text-white hover:bg-red-900 rounded-lg transition-colors" title="Eliminar Fuente">
                      <Trash2 class="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="px-6 py-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 flex justify-between items-center">
          <span>Mostrando {{ filteredList.length }} fuentes registradas</span>
        </div>
      </div>
    </div>

    <Form
      :show="showModal"
      :sourceToEdit="selectedItem"
      @close="showModal = false"
      @saved="loadData"
    />

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Search, Plus, FileText, Trash2, Landmark } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import Swal from 'sweetalert2';
import { useToast } from '@/composables/useToast';
import Form from './Form.vue';

const list = ref([]);
const isLoading = ref(true);
const searchTerm = ref('');
const statusFilter = ref('');
const showModal = ref(false);
const selectedItem = ref(null);
const { showSuccess, showError } = useToast();

const loadData = async () => {
  isLoading.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    if (!user?.company) return;

    // RUTA CON GUION BAJO SEGÚN INSTRUCCIÓN
    const res = await apiClient.get(`/funding_sources?company_id=${user.company}`);
    list.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    console.error(e);
    showError('Error cargando el catálogo de fuentes');
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

const filteredList = computed(() => {
  let result = list.value;
  if (statusFilter.value) result = result.filter(i => i.status === statusFilter.value);
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    result = result.filter(i => i.name?.toLowerCase().includes(term));
  }
  return result;
});

const getStatusClasses = (status) => {
  const map = {
    'ACTIVE': 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400',
    'INACTIVE': 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400',
  };
  return map[status] || map['INACTIVE'];
};

const getStatusDot = (status) => {
  return status === 'ACTIVE' ? 'bg-emerald-500' : 'bg-slate-400';
};

const getStatusLabel = (status) => {
  return status === 'ACTIVE' ? 'Activo' : 'Inactivo';
};

const openCreate = () => { selectedItem.value = null; showModal.value = true; };
const openEdit = (item) => { selectedItem.value = item; showModal.value = true; };

const deleteItem = (item) => {
  Swal.fire({
    title: '¿Eliminar Fuente?',
    text: "Si esta fuente ya está asignada a un producto crediticio, podría afectar la integridad contable.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (res) => {
    if (res.isConfirmed) {
      try {
        // RUTA CON GUION BAJO SEGÚN INSTRUCCIÓN
        await apiClient.delete(`/funding_sources/${item.id}`);
        list.value = list.value.filter(i => i.id !== item.id);
        showSuccess('Fuente eliminada correctamente.');
      } catch (e) {
        showError('No se pudo eliminar la fuente. Puede que esté en uso.');
      }
    }
  });
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
</style>